// branding.ts - Centralized branding and theming for the app

// For simplicity, we hardcode the values here. In a real app, you could parse branding.txt or load from a config API.
export const branding = {
  // Core brand colors
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  textColor: '#1f2937',
  backgroundColor: '#ffffff',

  // Sidebar configuration
  sidebar: {
    width: '360px',
    logoText: 'Zogzila Client',
    background: '#ffffff', // Changed to white
    padding: '48px',
    textColor: '#000000', // Changed to black
    activeBackground: '#a6c6f5', // Changed to light blue highlight
    logo: {
      fontSize: '24px',
      fontWeight: 700,
      marginBottom: '48px',
      color: '#000000' // Changed to black for white background
    },
    title: {
      fontSize: '36px',
      fontWeight: 700,
      marginBottom: '24px',
      color: '#000000' // Changed to black for white background
    },
    text: {
      fontSize: '16px',
      lineHeight: 1.6,
      color: '#000000' // Changed to black for white background
    },
    footer: {
      fontSize: '14px',
      color: '#6b7280' // Changed to gray for white background
    }
  },

  // Form styling
  forms: {
    input: {
      height: '40px',
      fontSize: '14px',
      padding: '8px 12px',
      borderRadius: '6px',
      borderColor: '#e5e7eb',
      background: '#ffffff',
      errorColor: '#ef4444',
      disabledOpacity: 0.6,
      focusBorderColor: '#2563eb',
      placeholderColor: '#9ca3af'
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '6px'
    },
    button: {
      height: '40px',
      padding: '0 24px',
      fontSize: '14px',
      fontWeight: 500,
      borderRadius: '6px',
      primary: {
        background: '#2563eb',
        color: '#ffffff',
        hoverBackground: '#1e40af'
      },
      secondary: {
        background: '#ffffff',
        color: '#2563eb',
        border: '#2563eb',
        hoverBackground: '#f3f4f6'
      }
    }
  },

  // Registration page specific styling
  registration: {
    main: {
      padding: '48px 64px',
      background: '#ffffff',
      maxWidth: '1000px'
    },
    stepper: {
      width: '600px',
      numberSize: '32px',
      fontSize: '14px',
      lineHeight: '20px',
      activeColor: '#2563eb',
      completedColor: '#10b981',
      inactiveColor: '#64748b',
      lineColor: '#e2e8f0',
      lineThickness: '2px'
    },
    content: {
      maxWidth: '800px',
      title: {
        fontSize: '24px',
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: '8px'
      },
      subtitle: {
        fontSize: '14px',
        color: '#6b7280'
      }
    }
  },

  // General Colors
  backgroundGradient: 'linear-gradient(135deg,rgb(248, 251, 255) 0%,rgb(29, 101, 209) 100%)',
  cardGradient: 'linear-gradient(135deg, #0a84ff 60%, #4fc3f7 100%)',
  accentColor: '#ffd700',
  successColor: '#00e676',
  successColorText: '#000000',
  errorColor: '#ff3b30',
  warningColor: '#f39c12',
  infoColor: '#8e44ad',
  assetColor: '#16a085',
  fontFamily: 'Inter, sans-serif',

  // Footer Configuration
  footer: {
    background: '#ffffff',
    textColor: '#64748b',
    linkColor: '#2563eb',
    height: '60px'
  },

  // Panels
  panels: {
    current: { label: 'Current', color: '#0a84ff' },
    pending: { label: 'Pending', color: '#ffd700' },
    blocked: { label: 'Blocked', color: '#ff3b30' },
    real: { label: 'Real', color: '#2c3e50' },
    crypto: { label: 'Crypto', color: '#8e44ad' },
    savings: { label: 'Savings', color: '#f39c12' },
    assets: { label: 'Assets', color: '#16a085' },
    mainAccount: { label: 'Main Account', color: '#0a84ff' },
    childWallet: { label: 'Child Wallet', color: '#121107' },
  },

  // Page Texts
  loginPage: {
    title: 'Welcome to Your Bank',
    button: 'Login',
    emailPlaceholder: 'Email',
    passwordPlaceholder: 'Password',
  },
  dashboardPage: {
    header: 'Good afternoon, Aristos 👋',
    subheader: 'Your personal banking dashboard',
    recentTransactions: 'Recent Transactions',
  },
  makePaymentPage: {
    title: 'Transfer Funds',
    payeeLabel: 'Payee',
    amountLabel: 'Amount',
    sendButton: 'Send',
    successMessage: 'Payment sent!',
  },
  transactionsPage: {
    title: 'Transactions',
    detailsHeader: 'Full Details',
  },
  childWalletPage: {
    title: "Child Wallet",
    balanceLabel: "Emily's Balance",
    recentActivity: 'Recent Activity',
  },
  payeesPage: {
    title: 'Payees',
    addPayeePlaceholder: 'Add new payee',
    addButton: 'Add',
  },
  settingsPage: {
    title: 'Settings',
    notifications: 'Enable Notifications',
    darkMode: 'Dark Mode',
  },
  cardsPage: {
    title: 'Your Cards',
    virtual: 'Virtual Card',
    physical: 'Physical Card',
  },
  accountPage: {
    title: 'Account Details',
    holder: 'Account Holder',
    iban: 'IBAN',
    bic: 'BIC',
    bank: 'Bank',
    address: 'Bank Address',
    country: 'Country',
    currency: 'Currency',
  },

  // Crypto Asset Statistics Section
  cryptoStats: {
    title: 'Crypto Asset Statistics',
    icon: '💹',
    color: '#8e44ad',
    totalValue: '$2,500.00',
    change24h: '+3.2%',
    change24hColor: '#27ae60',
    columns: [
      { label: 'Coin', key: 'coin' },
      { label: 'Value', key: 'value' },
      { label: 'Allocation', key: 'allocation' },
    ],
    coins: [
      { coin: 'Bitcoin (BTC)', value: '$1,400', allocation: '56%' },
      { coin: 'Ethereum (ETH)', value: '$800', allocation: '32%' },
      { coin: 'Solana (SOL)', value: '$200', allocation: '8%' },
      { coin: 'Other', value: '$100', allocation: '4%' },
    ],
    allocationBreakdown: [
      { label: 'BTC', width: '56%', color: '#f1c40f' },
      { label: 'ETH', width: '32%', color: '#2980b9' },
      { label: 'SOL', width: '8%', color: '#16a085' },
      { label: 'Other', width: '4%', color: '#888' },
    ],
  },

  // Commodities Statistics Section
  commoditiesStats: {
    title: 'Commodities Statistics',
    icon: '📊',
    color: '#f1c40f',
    columns: [
      { label: 'Commodity', key: 'commodity' },
      { label: 'Value', key: 'value' },
      { label: 'Change', key: 'change' },
      { label: 'Allocation', key: 'allocation' },
    ],
    commodities: [
      { commodity: 'Gold', value: '$3,200', change: '+1.5%', changeColor: '#27ae60', allocation: '40%' },
      { commodity: 'Silver', value: '$1,100', change: '+0.8%', changeColor: '#27ae60', allocation: '18%' },
      { commodity: 'Orange Juice', value: '$700', change: '-0.4%', changeColor: '#c0392b', allocation: '9%' },
      { commodity: 'Steel', value: '$1,800', change: '+2.1%', changeColor: '#27ae60', allocation: '23%' },
      { commodity: 'Other', value: '$1,000', change: '0.0%', changeColor: '#888', allocation: '10%' },
    ],
    allocationBreakdown: [
      { label: 'Gold', width: '40%', color: '#f1c40f' },
      { label: 'Silver', width: '18%', color: '#bdc3c7' },
      { label: 'OJ', width: '9%', color: '#e67e22' },
      { label: 'Steel', width: '23%', color: '#7f8c8d' },
      { label: 'Other', width: '10%', color: '#888' },
    ],
  },

  // Viewport and Layout
  viewport: {
    // Base widths
    mainMaxWidth: 900, // px
    headerMaxWidth: 1200, // px
    
    // Global browser zoom control (affects entire application)
    globalZoom: {
      factor: 0.9, // 1 = 100%, 0.8 = 80% zoom out, 1.2 = 120% zoom in
      minFactor: 0.5, // Minimum zoom (50% - very small)
      maxFactor: 2.0, // Maximum zoom (200% - very large)
      unit: 'scale', // CSS transform scale unit
    },

    // Global zoom transform (for CSS)
    get globalZoomTransform() {
      const zoom = Math.min(Math.max(this.globalZoom.factor, this.globalZoom.minFactor), this.globalZoom.maxFactor);
      return `${this.globalZoom.unit}(${zoom})`;
    },
  },

  // Header Styles
  headerBackground: 'transparent', // Set to 'transparent' for see-through, or any CSS color/gradient

  // Card Styles
  cardBackground: '#fff', // Classic solid white card background

  // New Global Styles
  pageBackground: '#f4f6f8',
  borderColor: '#e9ecef',

  header: {
    height: '64px',
    background: '#ffffff',
    borderColor: '#e9ecef',
    textColor: '#1f2937'
  },

  // Currency Settings
  currency: {
    symbol: '€',
    code: 'EUR'
  },

  // Session/Timeout Settings
  session: {
    inactivityTimeoutMinutes: 15
  },

  // Crypto Page Configuration
  cryptoPage: {
    title: 'Crypto Portfolio',
    subtitle: 'Track and manage your cryptocurrency investments',
    
    // Page Layout
    layout: {
      maxWidth: '900px',
      padding: '0 16px',
      marginTop: {
        mobile: '0',
        desktop: '32px'
      },
      paddingBottom: {
        mobile: '80px',
        desktop: '0'
      }
    },

    // Header Section
    header: {
      title: {
        fontSize: {
          mobile: '24px',
          desktop: '28px'
        },
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: '8px'
      },
      subtitle: {
        fontSize: {
          mobile: '15px',
          desktop: '16px'
        },
        color: '#666666'
      }
    },

    // Timeframe Buttons
    timeframe: {
      button: {
        padding: '8px 16px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 600,
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        active: {
          borderColor: '#8e44ad',
          background: '#8e44ad',
          color: '#ffffff'
        },
        inactive: {
          borderColor: '#eeeeee',
          background: 'transparent',
          color: '#666666'
        }
      }
    },

    // Portfolio Summary Card
    portfolioSummary: {
      background: '#8e44ad',
      borderRadius: '20px',
      padding: {
        mobile: '24px',
        desktop: '32px'
      },
      color: '#ffffff',
      boxShadow: '0 4px 24px rgba(142,68,173,0.15)',
      
      totalValue: {
        label: {
          fontSize: {
            mobile: '14px',
            desktop: '16px'
          },
          opacity: 0.9,
          marginBottom: '8px'
        },
        amount: {
          fontSize: {
            mobile: '32px',
            desktop: '36px'
          },
          fontWeight: 700
        }
      },
      
      change24h: {
        label: {
          fontSize: {
            mobile: '14px',
            desktop: '16px'
          },
          opacity: 0.9,
          marginBottom: '8px'
        },
        percentage: {
          fontSize: {
            mobile: '20px',
            desktop: '24px'
          },
          fontWeight: 700,
          color: '#ffffff'
        },
        value: {
          fontSize: {
            mobile: '14px',
            desktop: '16px'
          },
          opacity: 0.9,
          marginLeft: '8px'
        }
      }
    },

    // Wallet Section
    wallets: {
      card: {
        background: '#ffffff',
        borderRadius: '20px',
        padding: {
          mobile: '24px',
          desktop: '32px'
        },
        marginBottom: '24px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
      },
      
      header: {
        title: {
          fontSize: {
            mobile: '18px',
            desktop: '20px'
          },
          fontWeight: 600,
          color: '#1f2937',
          margin: '0'
        }
      },
      
      createButton: {
        padding: '8px 16px',
        borderRadius: '12px',
        border: 'none',
        background: '#2563eb',
        color: '#ffffff',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 600
      },
      
      walletCard: {
        padding: '16px',
        border: '1px solid #eeeeee',
        borderRadius: '12px',
        background: '#f8f9fa'
      },
      
      blockchainTag: {
        padding: '4px 8px',
        background: '#2563eb',
        color: '#ffffff',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 600
      },
      
      addressBox: {
        fontSize: '13px',
        fontFamily: 'monospace',
        background: '#ffffff',
        padding: '8px',
        borderRadius: '6px',
        border: '1px solid #eeeeee',
        marginBottom: '8px'
      },
      
      qrCode: {
        container: {
          padding: '8px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #eeeeee'
        },
        size: {
          mobile: 100,
          desktop: 120
        },
        helperText: {
          fontSize: '10px',
          color: '#666666',
          fontWeight: 500
        }
      },
      
      copyButton: {
        padding: '4px 8px',
        fontSize: '11px',
        border: '1px solid #dddddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 500,
        transition: 'all 0.2s',
        normal: {
          background: '#ffffff',
          color: '#666666'
        },
        copied: {
          background: '#e8f5e9',
          color: '#2e7d32'
        },
        hover: {
          background: '#f5f5f5'
        }
      }
    },

    // Coin Cards
    coinCards: {
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      },
      
      card: {
        background: '#ffffff',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
      },
      
      coinIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        color: '#ffffff',
        fontSize: '20px',
        fontWeight: 700
      },
      
      coinName: {
        fontWeight: 600,
        fontSize: '18px',
        color: '#1f2937'
      },
      
      coinSymbol: {
        color: '#666666',
        fontSize: '14px'
      },
      
      coinValue: {
        fontWeight: 700,
        fontSize: '20px',
        color: '#1f2937'
      },
      
      coinChange: {
        fontSize: '14px',
        fontWeight: 600
      },
      
      details: {
        borderTop: '1px solid #eeeeee',
        paddingTop: '16px',
        label: {
          color: '#666666',
          fontSize: '14px'
        },
        value: {
          fontWeight: 600,
          color: '#1f2937'
        }
      }
    },

    // Action Buttons
    actionButtons: {
      container: {
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      },
      
      base: {
        padding: '12px 24px',
        borderRadius: '12px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      
      buy: {
        background: '#a349a4',
        color: '#ffffff'
      },
      
      send: {
        background: '#2563eb',
        color: '#ffffff'
      },
      
      receive: {
        background: '#2563eb',
        color: '#ffffff'
      }
    },

    // Transaction Table
    transactionTable: {
      container: {
        background: '#ffffff',
        borderRadius: '20px',
        padding: {
          mobile: '24px',
          desktop: '32px'
        },
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        marginBottom: '32px'
      },
      
      title: {
        fontSize: {
          mobile: '18px',
          desktop: '20px'
        },
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: '24px'
      },
      
      table: {
        width: '100%',
        minWidth: '600px',
        borderCollapse: 'collapse',
        fontSize: {
          mobile: '14px',
          desktop: '16px'
        }
      },
      
      header: {
        color: '#666666',
        fontWeight: 600,
        padding: '12px 16px',
        borderBottom: '1px solid #eeeeee',
        whiteSpace: 'nowrap'
      },
      
      cell: {
        padding: '12px 16px',
        borderBottom: '1px solid #eeeeee',
        whiteSpace: 'nowrap'
      },
      
      typeTag: {
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: {
          mobile: '13px',
          desktop: '14px'
        },
        fontWeight: 600,
        buy: {
          background: '#e8f5e8',
          color: '#00e676'
        },
        sell: {
          background: '#ffe8ec',
          color: '#ff3b30'
        }
      }
    },

    // Modal Styling
    modal: {
      overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '24px'
      },
      
      container: {
        background: '#ffffff',
        borderRadius: '24px',
        padding: '32px',
        maxWidth: '480px',
        width: '100%'
      },
      
      title: {
        fontSize: '24px',
        fontWeight: 700,
        marginBottom: '24px',
        color: '#1f2937'
      },
      
      label: {
        display: 'block',
        marginBottom: '8px',
        color: '#666666',
        fontSize: '14px',
        fontWeight: 500
      },
      
      input: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: '1.5px solid #eeeeee',
        fontSize: '16px',
        marginBottom: '24px'
      },
      
      select: {
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: '1.5px solid #eeeeee',
        fontSize: '16px',
        marginBottom: '24px'
      },
      
      summary: {
        padding: '16px',
        background: '#f7fafd',
        borderRadius: '12px',
        marginBottom: '24px'
      },
      
      buttonGroup: {
        display: 'flex',
        gap: '12px'
      },
      
      primaryButton: {
        flex: 1,
        padding: '12px',
        borderRadius: '12px',
        border: 'none',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer'
      },
      
      secondaryButton: {
        padding: '12px',
        borderRadius: '12px',
        border: '1.5px solid #eeeeee',
        background: '#ffffff',
        color: '#666666',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer'
      }
    },

    // Status Colors
    status: {
      confirmed: {
        background: '#e8f5e9',
        color: '#2e7d32'
      },
      pending: {
        background: '#fff3e0',
        color: '#f57c00'
      },
      mainnet: {
        background: '#e8f5e9',
        color: '#2e7d32'
      },
      testnet: {
        background: '#fff3e0',
        color: '#f57c00'
      }
    }
  }
}; 